"use client";

import React, { memo, useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { VybeAgentMark } from "./VybeAgentMark";

/** Assistant tool part for client-side `vybe_dsl` (matches AI SDK UI shape). */
export interface VybeDslToolPart {
  type: "tool-vybe_dsl";
  toolCallId: string;
  state:
    | "input-streaming"
    | "input-available"
    | "output-available"
    | "output-error";
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

function dslCallPreview(input: unknown): string {
  const obj = input as { call?: unknown } | undefined;
  return typeof obj?.call === "string" ? obj.call : "";
}

function toolStateLabel(state: VybeDslToolPart["state"]): string {
  switch (state) {
    case "input-streaming":
      return "Parsing…";
    case "input-available":
      return "Running…";
    case "output-available":
      return "Done";
    case "output-error":
      return "Error";
    default:
      return state;
  }
}

interface DslToolBubbleProps {
  part: VybeDslToolPart;
}

function DslToolBubbleImpl({ part }: DslToolBubbleProps) {
  const [expanded, setExpanded] = useState(false);
  const onToggle = useCallback(() => setExpanded((v) => !v), []);

  const preview =
    dslCallPreview(part.input) || (part.state === "input-streaming" ? "…" : "");

  const badgeClass =
    part.state === "output-error"
      ? "bg-destructive/15 text-destructive border-destructive/30"
      : part.state === "output-available"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25"
        : "bg-muted text-muted-foreground border-border";

  // Only serialize the (potentially large) tool output when the bubble is expanded.
  const detailJson = useMemo(() => {
    if (!expanded) return "";
    try {
      if (part.state === "output-available") {
        return JSON.stringify(part.output, null, 2);
      }
      if (part.state === "output-error") {
        return part.errorText ?? "";
      }
      return "";
    } catch {
      return String(part.output ?? part.errorText ?? "");
    }
  }, [expanded, part.state, part.output, part.errorText]);

  return (
    <div className="flex min-w-0 gap-2.5">
      <div className="mt-0.5 shrink-0">
        <VybeAgentMark size={14} tone="primary" />
      </div>
      <div className="flex-1 min-w-0 rounded-md border border-border bg-muted/20 overflow-hidden">
        <button
          type="button"
          onClick={onToggle}
          title={preview || undefined}
          className="flex w-full min-w-0 items-center gap-2 overflow-hidden px-2 py-1.5 text-left hover:bg-muted/40 transition-colors"
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          <span className="min-w-0 flex-1 truncate text-[10px] font-mono text-foreground">
            {preview ? `> ${preview}` : "> vybe_dsl"}
          </span>
          <span
            className={cn(
              "text-[9px] uppercase tracking-wide shrink-0 rounded border px-1 py-px",
              badgeClass,
            )}
          >
            {toolStateLabel(part.state)}
          </span>
        </button>
        {expanded ? (
          <div className="border-t border-border px-2 py-2 bg-background/40">
            <div className="text-[10px] text-muted-foreground mb-1">Result</div>
            <pre className="max-h-48 max-w-full overflow-auto text-[10px] leading-snug font-mono whitespace-pre-wrap break-all">
              {detailJson || "—"}
            </pre>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export const DslToolBubble = memo(DslToolBubbleImpl);
DslToolBubble.displayName = "DslToolBubble";
